import { darken } from "polished";
import * as React from "react";
import { ColorResult } from "react-color";
import { useDispatch } from "redux-react-hook";

import Grid from "src/library/components/Hedron";
import { ThemeType } from "src/library/utils/theme";
import {
  ISettingsResponse,
  UpdateGlobalSettingParams,
  useGlobalSettingsApi
} from "src/core/shared/api/globalSettings.api";
import { setAppSettings } from "src/core/shared/store/global/global.actions";
import { OrderStatusGroupType } from "src/core/orders/api/orders.api";

import ColorPickerButton from "./ColorPickerButton";
import Section from "./Section";
import Setting from "./Setting";

interface ThemeColorsOverride {
  primaryDark: string;
  primaryLight: string;
  accentLight: string;
}

type OrderStatusGroupColor = "draftOrder" | "provisionalOrder" | "updatingOrder" | "dueOrder" | "errorOrder" | "cancelledOrder";

const ThemeColors: React.ReactFCWithChildren = () => {
  const [ globalSettings, globalSettingsApi ] = useGlobalSettingsApi();
  const [ updateGlobalSettingsResponse, updateGlobalSettings ] = globalSettingsApi<ISettingsResponse<"theme", ThemeType>>("UPDATE_SETTING");

  // generate color palette from selected colour
  const updateThemeColors = ({ hex: primaryLight }: ColorResult) => {
    const updatedThemeColors: ThemeColorsOverride = {
      primaryDark: darken(0.1, primaryLight),
      primaryLight,
      accentLight: "#E5BC1F"
    };

    // merge generated colors into updated theme object
    if (globalSettings && globalSettings.theme) {
      const updatedTheme: ThemeType = {
        ...globalSettings.theme,
        colors: {
          ...globalSettings.theme.colors,
          ...updatedThemeColors
        }
      };

      // push updated theme to db
      updateGlobalSettings<UpdateGlobalSettingParams<ThemeType>>({
        name: "theme",
        data: updatedTheme
      });
    }
  };

  const updateOrderStatusColor = (status: OrderStatusGroupType) => ({ hex: updatedOrderStatusColor }: ColorResult) => {
    const orderStatusGroupColor = `${status.toLowerCase()}Order` as OrderStatusGroupColor;

    if (globalSettings && globalSettings.theme) {
      // clone theme object
      const updatedTheme: ThemeType = { ...globalSettings.theme };

      // replace order status color
      updatedTheme.colors[ orderStatusGroupColor ] = updatedOrderStatusColor;

      // push updated theme to db
      updateGlobalSettings<UpdateGlobalSettingParams<ThemeType>>({
        name: "theme",
        data: updatedTheme
      });
    }
  };

  const dispatch = useDispatch();

  // on successful theme change in db, update redux store
  React.useEffect(() => {
    if (globalSettings && updateGlobalSettingsResponse.data) {
      const updatedTheme: ThemeType = updateGlobalSettingsResponse.data.setting;

      dispatch(
        setAppSettings({
          ...globalSettings,
          theme: updatedTheme
        })
      );
    }
  }, [ updateGlobalSettingsResponse.data ]);

  return (
    <Section title="Colours">
      <Setting description="Customise the main colour of the sidebar and buttons">
        <ColorPickerButton
          id="primary-picker"
          label="Primary brand colour"
          color="primaryDark"
          defaultColor="#1F1263"
          onSelect={updateThemeColors}
        />
      </Setting>

      <Setting description="Customise the colour of your order status groups">
        <Grid.Bounds direction="horizontal">
          <ColorPickerButton
            id="draft-picker"
            label="Draft"
            color="draftOrder"
            defaultColor="#3E4A5D"
            onSelect={updateOrderStatusColor("DRAFT")}
          />

          <ColorPickerButton
            id="provisional-picker"
            label="Provisional"
            color="provisionalOrder"
            defaultColor="#FFC06A"
            onSelect={updateOrderStatusColor("PROVISIONAL")}
          />

          <ColorPickerButton
            id="updating-picker"
            label="Updating"
            color="updatingOrder"
            defaultColor="#4553CA"
            onSelect={updateOrderStatusColor("UPDATING")}
          />

          <ColorPickerButton
            id="due-picker"
            label="Due"
            color="dueOrder"
            defaultColor="#4AD991"
            onSelect={updateOrderStatusColor("DUE")}
          />

          <ColorPickerButton
            id="cancelled-picker"
            label="Cancelled"
            color="cancelledOrder"
            defaultColor="#FF7285"
            onSelect={updateOrderStatusColor("CANCELLED")}
          />

          <ColorPickerButton
            id="error-picker"
            label="Error"
            color="errorOrder"
            defaultColor="#FF7285"
            onSelect={updateOrderStatusColor("ERROR")}
          />
        </Grid.Bounds>
      </Setting>
    </Section>
  );
};

export default ThemeColors;
